import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

const ALLOCATION_COLORS = [
  '#298171',
  '#F27534',
  '#4695B7',
  '#826AF9',
  '#FFE700',
  '#FF6C40',
  '#2CD9C5',
  '#54D62C',
];

const UNASSIGNED_COLOR = '#DFE3E8';

export { ALLOCATION_COLORS, UNASSIGNED_COLOR };

const AllocationBar = ({
  assignedRecipients = [],
  totalWeight = 0,
  recipients = [],
  unit = 'lbs',
  colorMap = new Map(),
}) => {
  const { t } = useTranslation('translation');

  if (totalWeight <= 0) {
    return (
      <Box
        sx={{
          height: 24,
          borderRadius: 1,
          backgroundColor: 'grey.200',
          width: '100%',
        }}
      />
    );
  }

  const totalAllocated = assignedRecipients.reduce(
    (acc, r) => acc + parseFloat(r.reservedWeight || 0),
    0
  );
  const isOverAllocated = totalAllocated > totalWeight;

  const getRecipientName = (teamId) => {
    if (teamId === 'unassigned') {
      return t('donations.available-to-others');
    }
    return recipients.find((r) => r.team_id === teamId)?.name || teamId;
  };

  const getColor = (teamId) => {
    return colorMap.get(teamId) || UNASSIGNED_COLOR;
  };

  const segments = assignedRecipients
    .filter((r) => r.teamId && parseFloat(r.reservedWeight || 0) > 0)
    .map((r) => ({
      teamId: r.teamId,
      weight: parseFloat(r.reservedWeight || 0),
      pct: (parseFloat(r.reservedWeight || 0) / totalWeight) * 100,
      name: getRecipientName(r.teamId),
      color: getColor(r.teamId),
    }));

  const unallocated = totalWeight - totalAllocated;
  if (unallocated > 0) {
    segments.push({
      teamId: '__unallocated__',
      weight: unallocated,
      pct: (unallocated / totalWeight) * 100,
      name: t('donations.not-yet-planned'),
      color: '#F4F6F8',
    });
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: 24,
        borderRadius: 1,
        overflow: 'hidden',
        width: '100%',
        backgroundColor: '#F4F6F8',
        border: isOverAllocated ? '2px solid' : '1px solid',
        borderColor: isOverAllocated ? 'error.main' : 'divider',
        transition: 'border-color 0.2s ease',
      }}
    >
      {segments.map((seg) => (
        <Tooltip
          key={seg.teamId}
          title={
            <Typography variant="caption">
              {seg.name}: {seg.weight} {unit}
            </Typography>
          }
          arrow
          placement="top"
        >
          <Box
            sx={{
              width: `${Math.min(seg.pct, 100)}%`,
              minWidth: seg.pct > 0 ? 4 : 0,
              backgroundColor: seg.color,
              transition: 'width 0.3s ease',
              cursor: 'default',
              '&:hover': {
                opacity: 0.85,
              },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
};

export default AllocationBar;
